import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import { environment } from '../../config/environment';
import './JobApplicationPage.css';
import Step1Personal from './Step1Personal';
import Step2Address from './Step2Address';
import Step3Education from './Step3Education';
import Step4Experience from './Step4Experience';
import Step5Upload from './Step5Upload';

const parseJsonSafe = async (response) => {
    try {
        return await response.json();
    } catch {
        return {};
    }
};

const defaultForm = {
    postId: '',
    candidateName: '',
    fatherOrHusbandName: '',
    email: '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    category: '',
    otp: '',
    permanentFullAddress: '',
    permanentCityTown: '',
    permanentDistrict: '',
    permanentState: '',
    permanentPincode: '',
    permanentCountry: 'India',
    addressSameAsCurrent: false,
    currentFullAddress: '',
    currentCityTown: '',
    currentDistrict: '',
    currentState: '',
    currentPincode: '',
    currentCountry: 'India',
    educationList: [],
    experienceType: '',
    experienceList: [
        {
            id: 1,
            companyName: '',
            designation: '',
            employmentType: '',
            startDate: '',
            endDate: '',
            currentlyWorking: false,
            experienceCertificate: '',
            experienceCertificateFile: null
        }
    ],
    profileImage: '',
    profileImageFile: null,
    profileImagePreview: '',
    resume: '',
    resumeFile: null,
    resumePreview: '',
    otherDocuments: [],
};

const JobApplicationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // ✅ currentStep ko 1 se start karo (Top se)
    const [currentStep, setCurrentStep] = useState(1);
    
    const [formData, setFormData] = useState(() => {
        const savedPostId = sessionStorage.getItem('jobAppSelectedPostId');
        return { ...defaultForm, postId: savedPostId || '' };
    });

    const [applicantId, setApplicantId] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [postOptions, setPostOptions] = useState([]);
    const [isPostsLoading, setIsPostsLoading] = useState(false);
    
    const [showOtpField, setShowOtpField] = useState(false);
    const [isStep1OtpVerified, setIsStep1OtpVerified] = useState(false);
    const [emailSentForOtp, setEmailSentForOtp] = useState('');

    useEffect(() => {
        if (formData.postId) {
            sessionStorage.setItem('jobAppSelectedPostId', formData.postId);
        }
    }, [formData.postId]);

    useEffect(() => {
        if (formData.maritalStatus && !['1', '2'].includes(String(formData.maritalStatus))) {
            setFormData((prev) => ({ ...prev, maritalStatus: '' }));
        }
    }, [formData.maritalStatus]);

    useEffect(() => {
        const fetchActivePostTitles = async () => {
            setIsPostsLoading(true);
            try {
                const response = await fetch(`${environment.baseUrl}${environment.endpoints.jobApplicationGetActiveTitle}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });
                const data = await parseJsonSafe(response);
                if (!response.ok || data?.response !== true) {
                    setPostOptions([]);
                    return;
                }
                const options = Array.isArray(data?.data)
                    ? data.data.filter((item) => item?.id != null && item?.title).map((item) => ({
                          id: String(item.id),
                          name: item.title,
                      }))
                    : [];
                setPostOptions(options);
            } catch {
                setPostOptions([]);
            } finally {
                setIsPostsLoading(false);
            }
        };
        fetchActivePostTitles();
    }, []);

    // ✅ Pre-fill job title from location state
    useEffect(() => {
        const passedJobId = location.state?.jobId;
        if (passedJobId) {
            setFormData((prev) => {
                if (prev.postId !== String(passedJobId)) {
                    return { ...prev, postId: String(passedJobId) };
                }
                return prev;
            });
        }
    }, [location.state?.jobId]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
        }
        
        if ((name === 'email' || name === 'mobileNumber') && isStep1OtpVerified) {
            setIsStep1OtpVerified(false);
            setShowOtpField(false);
            setEmailSentForOtp('');
        } else if (name === 'email' && emailSentForOtp && value.trim() !== emailSentForOtp) {
            setShowOtpField(false);
            setIsStep1OtpVerified(false);
            setEmailSentForOtp('');
        }
    };

    const validateStepOne = () => {
        const nextErrors = {};
        if (!formData.postId) nextErrors.postId = 'Please select a post.';
        if (!formData.candidateName.trim()) nextErrors.candidateName = 'Candidate name is required.';
        if (!formData.fatherOrHusbandName.trim()) nextErrors.fatherOrHusbandName = "Father's / Husband's name is required.";
        if (!formData.email.trim()) {
            nextErrors.email = 'Email is required.';
        } else if (!/^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim())) {
            nextErrors.email = 'Enter a valid email.';
        }
        if (!formData.mobileNumber.trim()) {
            nextErrors.mobileNumber = 'Mobile number is required.';
        } else if (!/^\d{10}$/.test(formData.mobileNumber.trim())) {
            nextErrors.mobileNumber = 'Enter a valid 10 digit mobile number.';
        }
        if (!formData.dateOfBirth) nextErrors.dateOfBirth = 'Date of birth is required.';
        if (!formData.gender) nextErrors.gender = 'Please select gender.';
        if (!formData.maritalStatus || !['1', '2'].includes(String(formData.maritalStatus))) {
            nextErrors.maritalStatus = 'Please select marital status.';
        }
        if (!formData.category) nextErrors.category = 'Please select category.';
        
        setFormErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateStepTwo = () => {
        const errors = {};
        if (!formData.currentFullAddress.trim()) errors.currentFullAddress = 'Full address is required.';
        if (!formData.currentCityTown.trim()) errors.currentCityTown = 'City/Town is required.';
        if (!formData.currentDistrict.trim()) errors.currentDistrict = 'District is required.';
        if (!formData.currentState.trim()) errors.currentState = 'State is required.';
        if (!formData.currentPincode.trim()) {
            errors.currentPincode = 'Pincode is required.';
        } else if (!/^\d{6}$/.test(formData.currentPincode.trim())) {
            errors.currentPincode = 'Enter a valid 6 digit pincode.';
        }
        
        if (!formData.addressSameAsCurrent) {
            if (!formData.permanentFullAddress.trim()) errors.permanentFullAddress = 'Full address is required.';
            if (!formData.permanentCityTown.trim()) errors.permanentCityTown = 'City/Town is required.';
            if (!formData.permanentDistrict.trim()) errors.permanentDistrict = 'District is required.';
            if (!formData.permanentState.trim()) errors.permanentState = 'State is required.';
            if (!formData.permanentPincode.trim()) {
                errors.permanentPincode = 'Pincode is required.';
            } else if (!/^\d{6}$/.test(formData.permanentPincode.trim())) {
                errors.permanentPincode = 'Enter a valid 6 digit pincode.';
            }
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStepThree = () => {
        const errors = {};
        let educationErrors = [];
        formData.educationList.forEach((edu, index) => {
            const eduErrors = {};
            if (!edu.educationLevel) eduErrors.educationLevel = 'Education level is required.';
            
            const isSchool = ['8th', '10th', '12th'].includes(edu.educationLevel);
            if (isSchool) {
                if (!edu.board?.trim()) eduErrors.board = 'Board name is required.';
            } else {
                if (!edu.university?.trim()) eduErrors.university = 'University is required.';
                if (!edu.college?.trim()) eduErrors.college = 'College is required.';
                if (!edu.courseName?.trim()) eduErrors.courseName = 'Course name is required.';
                if (!edu.courseType?.trim()) eduErrors.courseType = 'Course type is required.';
            }
            
            if (!edu.percentage?.trim()) {
                eduErrors.percentage = 'Percentage is required.';
            } else if (isNaN(edu.percentage.trim()) || parseFloat(edu.percentage) < 0 || parseFloat(edu.percentage) > 100) {
                eduErrors.percentage = 'Enter a valid percentage (0-100).';
            }
            
            if (!edu.yearOfPassing?.trim()) {
                eduErrors.yearOfPassing = 'Year of passing is required.';
            } else if (!/^\d{4}$/.test(edu.yearOfPassing.trim())) {
                eduErrors.yearOfPassing = 'Enter a valid year (YYYY).';
            }

            if (!edu.marksheet) eduErrors.marksheet = 'Marksheet is required.';
            if (!isSchool && edu.educationLevel !== '8th' && !edu.certificate) {
                eduErrors.certificate = 'Certificate is required.';
            }

            if (Object.keys(eduErrors).length > 0) {
                educationErrors[index] = eduErrors;
            }
        });
        
        if (educationErrors.length > 0) errors.educationList = educationErrors;
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStepFour = () => {
        const errors = {};
        if (!formData.experienceType) {
            errors.experienceType = 'Please select whether you are a fresher or experienced.';
            setFormErrors(errors);
            return false;
        }
        if (formData.experienceType === 'fresher') {
            setFormErrors(errors);
            return true;
        }
        
        let experienceErrors = [];
        formData.experienceList.forEach((exp, index) => {
            const expErrors = {};
            if (!exp.companyName?.trim()) expErrors.companyName = 'Company name is required.';
            if (!exp.designation?.trim()) expErrors.designation = 'Designation is required.';
            if (!exp.employmentType?.trim()) expErrors.employmentType = 'Employment type is required.';
            if (!exp.startDate?.trim()) expErrors.startDate = 'Start date is required.';
            if (!exp.currentlyWorking && !exp.endDate?.trim()) expErrors.endDate = 'End date is required.';
            
            if (exp.startDate && exp.endDate && new Date(exp.endDate) < new Date(exp.startDate)) {
                expErrors.endDate = 'End date cannot be before start date.';
            }
            
            if (Object.keys(expErrors).length > 0) {
                experienceErrors[index] = expErrors;
            }
        });
        
        if (experienceErrors.length > 0) errors.experienceList = experienceErrors;
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStepFive = () => {
        const errors = {};
        if (!formData.profileImageFile) errors.profileImage = 'Profile image is required.';
        if (!formData.resumeFile) errors.resume = 'Resume is required.';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNextStep = async () => {
        if (currentStep === 1) {
            if (!validateStepOne()) return;

            if (!showOtpField && !isStep1OtpVerified) {
                setIsSubmitting(true);
                try {
                    const otpResponse = await fetch(`${environment.baseUrl}${environment.endpoints.jobApplicationSendOtp}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: formData.email.trim() })
                    });
                    const otpData = await parseJsonSafe(otpResponse);
                    
                    if (otpResponse.ok && otpData?.response === true) {
                        setEmailSentForOtp(formData.email.trim());
                        setShowOtpField(true);
                        await Swal.fire({
                            icon: 'success',
                            text: otpData?.message || 'OTP sent successfully to your email!',
                            confirmButtonColor: '#0f7a8f',
                            timer: 3000,
                            timerProgressBar: true,
                        });
                    } else {
                        throw new Error(otpData?.message || 'Failed to send OTP.');
                    }
                } catch (error) {
                    await Swal.fire({ icon: 'error', text: error?.message || 'Failed to send OTP. Please try again.', confirmButtonColor: '#c7374e' });
                } finally {
                    setIsSubmitting(false);
                }
                return;
            }
            
            if (!isStep1OtpVerified) {
                Swal.fire({ icon: 'warning', text: 'Please verify OTP first', confirmButtonColor: '#0f7a8f' });
                return;
            }

            setIsSubmitting(true);
            try {
                const payload1 = {
                    post_id: Number(formData.postId),
                    candidate_name: formData.candidateName.trim(),
                    father_or_husband_name: formData.fatherOrHusbandName.trim(),
                    email: formData.email.trim(),
                    mobile_number: formData.mobileNumber.trim(),
                    dob: formData.dateOfBirth,
                    gender: formData.gender,
                    marital_status: formData.maritalStatus,
                    category: formData.category,
                };
                if (applicantId) payload1.applicant_id = applicantId;

                const response = await fetch(`${environment.baseUrl}${environment.endpoints.submitStep1}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload1)
                });
                const data = await parseJsonSafe(response);

                if (!response.ok || data?.response !== true) {
                    throw new Error(data?.message || 'Failed to save Personal Details.');
                }

                if (data?.data?.applicant_id) {
                    setApplicantId(data?.data?.applicant_id);
                } else if (data?.applicant_id) {
                    setApplicantId(data.applicant_id);
                }

                setCurrentStep(2);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                await Swal.fire({ icon: 'error', text: error?.message || 'Error saving Step 1', confirmButtonColor: '#c7374e' });
            } finally {
                setIsSubmitting(false);
            }
        } 
        else if (currentStep === 2) {
            if (!validateStepTwo()) return;

            setIsSubmitting(true);
            try {
                const currentAddr = {
                    full_address: formData.currentFullAddress.trim(),
                    city_town: formData.currentCityTown.trim(),
                    district: formData.currentDistrict.trim(),
                    state: formData.currentState.trim(),
                    pincode: formData.currentPincode.trim(),
                    country: formData.currentCountry.trim(),
                };

                const payload2 = {
                    applicant_id: applicantId,
                    current_address: currentAddr,
                    permanent_address: formData.addressSameAsCurrent ? currentAddr : {
                        full_address: formData.permanentFullAddress.trim(),
                        city_town: formData.permanentCityTown.trim(),
                        district: formData.permanentDistrict.trim(),
                        state: formData.permanentState.trim(),
                        pincode: formData.permanentPincode.trim(),
                        country: formData.permanentCountry.trim(),
                    },
                    address_same_as_current: formData.addressSameAsCurrent
                };

                const response = await fetch(`${environment.baseUrl}${environment.endpoints.submitStep2}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload2)
                });
                const data = await parseJsonSafe(response);

                if (!response.ok || data?.response !== true) throw new Error(data?.message || 'Failed to save Address Details.');

                setCurrentStep(3);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                await Swal.fire({ icon: 'error', text: error?.message || 'Error saving Step 2', confirmButtonColor: '#c7374e' });
            } finally {
                setIsSubmitting(false);
            }
        } 
        else if (currentStep === 3) {
            if (!validateStepThree()) return;

            setIsSubmitting(true);
            try {
                const formDataPayload = new FormData();
                formDataPayload.append('applicant_id', applicantId);
                
                const sanitizedEducationList = formData.educationList.map((edu, index) => {
                    const clonedEdu = { ...edu };
                    if (clonedEdu.marksheetFile) {
                        formDataPayload.append(`marksheet_${index}`, clonedEdu.marksheetFile);
                        clonedEdu.marksheet = `marksheet_${index}`;
                        delete clonedEdu.marksheetFile;
                        delete clonedEdu.marksheetPreview;
                    }
                    if (clonedEdu.certificateFile) {
                        formDataPayload.append(`certificate_${index}`, clonedEdu.certificateFile);
                        clonedEdu.certificate = `certificate_${index}`;
                        delete clonedEdu.certificateFile;
                        delete clonedEdu.certificatePreview;
                    }
                    return clonedEdu;
                });

                formDataPayload.append('education_list', JSON.stringify(sanitizedEducationList));

                const response = await fetch(`${environment.baseUrl}${environment.endpoints.submitStep3}`, {
                    method: 'POST',
                    body: formDataPayload
                });
                const data = await parseJsonSafe(response);

                if (!response.ok || data?.response !== true) throw new Error(data?.message || 'Failed to save Education Details.');

                setCurrentStep(4);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                await Swal.fire({ icon: 'error', text: error?.message || 'Error saving Step 3', confirmButtonColor: '#c7374e' });
            } finally {
                setIsSubmitting(false);
            }
        } 
        else if (currentStep === 4) {
            if (!validateStepFour()) return;

            setIsSubmitting(true);
            try {
                const formDataPayload = new FormData();
                formDataPayload.append('applicant_id', applicantId);
                formDataPayload.append('experience_type', formData.experienceType);
                
                const sanitizedExperienceList = formData.experienceType === 'fresher' ? [] : formData.experienceList.map((exp, index) => {
                    const clonedExp = { ...exp };
                    if (clonedExp.experienceCertificateFile) {
                        formDataPayload.append(`experience_certificate_${index}`, clonedExp.experienceCertificateFile);
                        clonedExp.experienceCertificate = `experience_certificate_${index}`;
                        delete clonedExp.experienceCertificateFile;
                        delete clonedExp.experienceCertificatePreview;
                    }
                    return clonedExp;
                });

                formDataPayload.append('experience_list', JSON.stringify(sanitizedExperienceList));

                const response = await fetch(`${environment.baseUrl}${environment.endpoints.submitStep4}`, {
                    method: 'POST',
                    body: formDataPayload
                });
                const data = await parseJsonSafe(response);

                if (!response.ok || data?.response !== true) throw new Error(data?.message || 'Failed to save Experience Details.');

                setCurrentStep(5);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                await Swal.fire({ icon: 'error', text: error?.message || 'Error saving Step 4', confirmButtonColor: '#c7374e' });
            } finally {
                setIsSubmitting(false);
            }
        } 
        else if (currentStep === 5) {
            if (!validateStepFive()) return;

            setIsSubmitting(true);
            try {
                const formDataPayload = new FormData();
                formDataPayload.append('applicant_id', applicantId);
                
                if (formData.profileImageFile) {
                    formDataPayload.append('profile_image', formData.profileImageFile);
                }
                if (formData.resumeFile) {
                    formDataPayload.append('resume', formData.resumeFile);
                }
                if (Array.isArray(formData.otherDocuments)) {
                    formData.otherDocuments.forEach((doc) => {
                        if (doc && doc.file) {
                            formDataPayload.append('other_documents', doc.file);
                        }
                    });
                }

                const response = await fetch(`${environment.baseUrl}${environment.endpoints.submitStep5}`, {
                    method: 'POST',
                    body: formDataPayload,
                });

                const data = await parseJsonSafe(response);

                if (!response.ok || data?.response !== true) {
                    throw new Error(data?.message || 'Submission failed.');
                }

                await Swal.fire({
                    icon: 'success',
                    title: 'Application Submitted!',
                    text: data?.message || 'Your application has been submitted successfully.',
                    confirmButtonColor: '#0f7a8f',
                });

                setFormData({ ...defaultForm, postId: formData.postId });
                setApplicantId('');
                setIsStep1OtpVerified(false);
                setShowOtpField(false);
                setCurrentStep(1);
                sessionStorage.removeItem('jobAppSelectedPostId');
                navigate('/');
                
            } catch (error) {
                await Swal.fire({ icon: 'error', title: 'Submission Failed', text: error?.message || 'Unable to submit application.', confirmButtonColor: '#c7374e' });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handlePrevStep = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleOtpVerified = (verified) => {
        setIsStep1OtpVerified(verified);
    };

    const handleEducationChange = (index, field, value) => {
        const updated = [...formData.educationList];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'educationLevel') {
            const isSchool = ['8th', '10th', '12th'].includes(value);
            if (isSchool) {
                delete updated[index].university; delete updated[index].college; delete updated[index].courseName; delete updated[index].courseType;
            } else {
                delete updated[index].board;
            }
        }
        setFormData((prev) => ({ ...prev, educationList: updated }));
        if (formErrors.educationList?.[index]) {
            const errors = { ...formErrors };
            delete errors.educationList[index][field];
            setFormErrors(errors);
        }
    };

    const addEducation = () => setFormData((prev) => ({ ...prev, educationList: [...prev.educationList, { id: Date.now(), educationLevel: '' }] }));
    const removeEducation = (index) => setFormData((prev) => ({ ...prev, educationList: prev.educationList.filter((_, i) => i !== index) }));
    
    const handleEducationFileUpload = (index, type, e) => {
        if(e.target.files[0]) {
            const updated = [...formData.educationList];
            updated[index][type] = e.target.files[0].name;
            updated[index][type + 'File'] = e.target.files[0]; 
            setFormData(prev => ({ ...prev, educationList: updated }));
        }
    };
    
    const handleRemoveFile = (index, type) => {
        const updated = [...formData.educationList];
        updated[index][type] = '';
        updated[index][type + 'File'] = null; 
        setFormData(prev => ({ ...prev, educationList: updated }));
    };

    const handleExperienceTypeChange = (val) => setFormData(prev => ({ ...prev, experienceType: val }));
    const handleExperienceChange = (index, field, value) => {
        const updated = [...formData.experienceList];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'currentlyWorking' && value === true) updated[index].endDate = '';
        setFormData(prev => ({ ...prev, experienceList: updated }));
    };
    
    const addExperience = () => setFormData(prev => ({ ...prev, experienceList: [...prev.experienceList, { id: Date.now(), currentlyWorking: false }] }));
    const removeExperience = (index) => setFormData(prev => ({ ...prev, experienceList: prev.experienceList.filter((_, i) => i !== index) }));
    
    const handleExperienceFileUpload = (index, e) => {
        if(e.target.files[0]) {
            const updated = [...formData.experienceList];
            updated[index].experienceCertificate = e.target.files[0].name;
            updated[index].experienceCertificateFile = e.target.files[0];
            setFormData(prev => ({ ...prev, experienceList: updated }));
        }
    };
    
    const handleRemoveExperienceFile = (index) => {
        const updated = [...formData.experienceList];
        updated[index].experienceCertificate = '';
        updated[index].experienceCertificateFile = null;
        setFormData(prev => ({ ...prev, experienceList: updated }));
    };

    const handleProfileImageUpload = (e) => {
        if(e.target.files[0]) {
            setFormData(prev => ({...prev, profileImage: e.target.files[0].name, profileImageFile: e.target.files[0], profileImagePreview: URL.createObjectURL(e.target.files[0])}));
        }
    };
    const handleResumeUpload = (e) => {
        if(e.target.files[0]) {
            setFormData(prev => ({...prev, resume: e.target.files[0].name, resumeFile: e.target.files[0]}));
        }
    };
    const handleOtherDocumentsUpload = (e) => {
        if(e.target.files.length > 0) {
            const newDocs = Array.from(e.target.files).map(f => ({ name: f.name, file: f }));
            setFormData(prev => ({ ...prev, otherDocuments: [...prev.otherDocuments, ...newDocs] }));
        }
    };

    return (
        <div className="job-application-page">
            <Header />
            <main className="job-application-main">
                <section className="application-form-card">
                    {/* ✅ CANDIDATE DETAILS FORM HEADING - TOP PAR */}
                    <h1 className="candidate-details-heading">CANDIDATE DETAILS FORM</h1>
                    
                    <div className="application-stepper">
                        <div className={`step-item ${currentStep >= 1 ? 'active' : ''}`}><span>1</span><p>Personal</p></div>
                        <div className={`step-item ${currentStep >= 2 ? 'active' : ''}`}><span>2</span><p>Address</p></div>
                        <div className={`step-item ${currentStep >= 3 ? 'active' : ''}`}><span>3</span><p>Education</p></div>
                        <div className={`step-item ${currentStep >= 4 ? 'active' : ''}`}><span>4</span><p>Experience</p></div>
                        <div className={`step-item ${currentStep >= 5 ? 'active' : ''}`}><span>5</span><p>Upload</p></div>
                    </div>
                    
                    <form className="application-form-grid" onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} noValidate>
                        {currentStep === 1 && (
                            <Step1Personal
                                formData={formData}
                                formErrors={formErrors}
                                handleChange={handleChange}
                                isSubmitting={isSubmitting}
                                postOptions={postOptions}
                                isPostsLoading={isPostsLoading}
                                onOtpVerified={handleOtpVerified}
                                showOtpField={showOtpField}
                                setShowOtpField={setShowOtpField}
                            />
                        )}
                        {currentStep === 2 && (
                            <Step2Address
                                formData={formData}
                                formErrors={formErrors}
                                handleChange={handleChange}
                                isSubmitting={isSubmitting}
                            />
                        )}
                        {currentStep === 3 && (
                            <Step3Education
                                formData={formData}
                                formErrors={formErrors}
                                handleEducationChange={handleEducationChange}
                                addEducation={addEducation}
                                removeEducation={removeEducation}
                                handleEducationFileUpload={handleEducationFileUpload}
                                handleRemoveFile={handleRemoveFile}
                                isSubmitting={isSubmitting}
                            />
                        )}
                        {currentStep === 4 && (
                            <Step4Experience
                                formData={formData}
                                formErrors={formErrors}
                                handleExperienceTypeChange={handleExperienceTypeChange}
                                handleExperienceChange={handleExperienceChange}
                                addExperience={addExperience}
                                removeExperience={removeExperience}
                                handleExperienceFileUpload={handleExperienceFileUpload}
                                handleRemoveExperienceFile={handleRemoveExperienceFile}
                                isSubmitting={isSubmitting}
                            />
                        )}
                        {currentStep === 5 && (
                            <Step5Upload
                                formData={formData}
                                formErrors={formErrors}
                                handleProfileImageUpload={handleProfileImageUpload}
                                handleRemoveProfileImage={() => setFormData(prev => ({...prev, profileImage: '', profileImageFile: null, profileImagePreview: ''}))}
                                handleResumeUpload={handleResumeUpload}
                                handleRemoveResume={() => setFormData(prev => ({...prev, resume: '', resumeFile: null}))}
                                handleOtherDocumentsUpload={handleOtherDocumentsUpload}
                                handleRemoveOtherDocument={(index) => setFormData(prev => ({...prev, otherDocuments: prev.otherDocuments.filter((_, i) => i !== index)}))}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        <div className="application-actions">
                            <button
                                type="button"
                                className="application-back-btn"
                                onClick={() => navigate(-1)}
                            >
                                ← Back to Jobs
                            </button>
                            
                            {currentStep > 1 && (
                                <button type="button" className="application-prev-btn" onClick={handlePrevStep} disabled={isSubmitting}>
                                    Previous
                                </button>
                            )}
                            
                            <button 
                                type="submit" 
                                className="application-submit-btn" 
                                disabled={isSubmitting || (currentStep === 1 && showOtpField && !isStep1OtpVerified)}
                            >
                                {isSubmitting
                                    ? 'Processing...'
                                    : currentStep === 1 && !showOtpField && !isStep1OtpVerified
                                        ? 'Send OTP'
                                        : currentStep === 5
                                            ? 'Submit Application'
                                            : 'Submit & Next'
                                }
                            </button>
                        </div>
                    </form>
                </section>
            </main>
        </div>
    );
};

export default JobApplicationPage;